import { format } from "date-fns"
import { MdDownload } from "react-icons/md"
import { SiMicrosoftexcel } from "react-icons/si"
import { FaRegFilePdf } from "react-icons/fa6"
import { Button } from "components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover"
import { Separator } from "components/ui/separator"
import { toast } from "components/ui/use-toast"
import { downloadExcel } from "@/utils/download-excel"
import { downloadPdf } from "@/utils/download-pdf"
import {
  useExportStoresConciliationsExcelMutation,
  useExportStoresConciliationsPdfMutation,
} from "@/services/store-conciliation"
import { formatFilterValue } from "@/utils/formatters"

interface ExportOptionsProps {
  filters: Record<string, any>
}

const exportButtonClass =
  "w-full flex items-center gap-3 px-3 py-3 text-sm text-left hover:bg-[#F4C83E]/20 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 group"

const iconContainerClass =
  "flex items-center justify-center w-8 h-8 bg-[#F4C83E]/20 rounded-lg group-hover:bg-[#F4C83E]/30 transition-colors"

export default function ExportOptions({ filters }: ExportOptionsProps) {
  const [exportExcel, { isLoading: isExportingExcel }] = useExportStoresConciliationsExcelMutation()
  const [exportPdf, { isLoading: isExportingPdf }] = useExportStoresConciliationsPdfMutation()

  const isExporting = isExportingExcel || isExportingPdf

  const getFilters = () => ({
    ...(filters.date && {
      transactionDate: format(new Date(filters.date as string), "yyyy-MM-dd"),
    }),
    ...(filters.code && { code: formatFilterValue(filters.code) }),
    ...(filters.responsible && { responsible: formatFilterValue(filters.responsible) }),
    ...(filters.status && { status: formatFilterValue(filters.status) }),
    ...(filters.regional_id && { regional_id: formatFilterValue(filters.regional_id) }),
    ...(filters.divergence_value_range && {
      divergence_value_range: formatFilterValue(filters.divergence_value_range),
    }),
    is_store_listing: true,
  })

  const handleExportExcel = async () => {
    try {
      const response = await exportExcel(getFilters()).unwrap()
      const fileName = `conciliacao-lojas-${format(new Date(), "yyyy-MM-dd")}.xlsx`
      downloadExcel(response, fileName)
      toast({ description: "Planilha Excel exportada com sucesso!", duration: 3000 })
    } catch {
      toast({ variant: "destructive", description: "Erro ao exportar planilha Excel. Tente novamente.", duration: 5000 })
    }
  }

  const handleExportPdf = async () => {
    try {
      const response = await exportPdf(getFilters()).unwrap()
      const fileName = `conciliacao-lojas-${format(new Date(), "yyyy-MM-dd")}.pdf`
      downloadPdf(response, fileName)
      toast({ description: "Relatório PDF exportado com sucesso!", duration: 3000 })
    } catch {
      toast({ variant: "destructive", description: "Erro ao exportar relatório PDF. Tente novamente.", duration: 5000 })
    }
  }

  return (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2 text-sm font-medium text-[#2B0C1A]">
        <span>Exportar Dados:</span>
      </div>

      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={isExporting}
            className="flex items-center gap-2 min-w-[160px] transition-all duration-200"
          >
            <MdDownload className="w-4 h-4" />
            {isExporting ? "Exportando..." : "Escolher Formato"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3 bg-white shadow-lg border">
          <div className="space-y-1">
            <div className="px-2 py-1 text-xs font-medium text-[#2B0C1A] uppercase tracking-wide">
              Formatos Disponíveis
            </div>
            <button onClick={handleExportExcel} disabled={isExporting} className={exportButtonClass}>
              <div className={iconContainerClass}>
                <SiMicrosoftexcel className="w-4 h-4 text-[#2B0C1A]" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-[#2B0C1A]">Planilha Excel</div>
                <div className="text-xs text-gray-600">Formato .xlsx para análise</div>
              </div>
            </button>

            <Separator className="my-2 bg-[#2B0C1A]/20" />

            <button onClick={handleExportPdf} disabled={isExporting} className={exportButtonClass}>
              <div className={iconContainerClass}>
                <FaRegFilePdf className="w-4 h-4 text-[#2B0C1A]" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-[#2B0C1A]">Relatório PDF</div>
                <div className="text-xs text-gray-600">Formato para impressão</div>
              </div>
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
